import { FC, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { getIndexes } from "@/controllers/stock-indexes";
import { getPulseBreadth } from "@/controllers/pulse";
import { useWebSocket } from "src/hooks/useWebSocket";
import Skeleton from "@/components/skeleton/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styles from "./MarketsPulse.module.scss";
import { MoversStrip } from "./Panels";
import { SessionMode, signed } from "./utils";
import { usePulseData } from "./usePulseData";

const IndexesChart = dynamic(() => import("@/components/indexes-chart/IndexCandlesChart"), { ssr: false });

type IndexRow = { name: string; dailyChange: number | null; universeId: number; ISIN: string };

// What the panel opens on: Europe while the US is shut, the S&P 500 once it opens.
const PREFERRED: Record<SessionMode, string[]> = {
    pre: ["STOXX50", "EURO STOXX 50", "DAX", "FTSE 100", "S&P 500"],
    us: ["S&P 500"],
    post: ["S&P 500"],
};

const IndexButton: FC<{ index: IndexRow; active: boolean; onSelect: () => void }> = ({ index, active, onSelect }) => {
    const [pch, setPch] = useState<number | null>(index.dailyChange ?? null);

    useWebSocket("multiWebSocket", `index-chart-${index.ISIN}`, JSON.stringify({ type: "subscribe", ISIN: index.ISIN, currency: "IND2", depth: "false" }), ({ data }: MessageEvent<any>) => {
        const parsed = JSON.parse(data);
        const value = parsed?.update?.pch;
        if (parsed.ISIN === index.ISIN && typeof value === "number") {
            setPch(value);
        }
    });

    const tone = pch === null ? "" : pch >= 0 ? styles.up : styles.down;
    return (
        <button className={`${styles.indexRow} ${active ? styles.indexRowActive : ""}`} onClick={onSelect} aria-pressed={active}>
            <span>{index.name}</span>
            <span className={`${styles.num} ${tone}`}>{pch === null ? "—" : signed(pch, 2, "%")}</span>
        </button>
    );
};

const BreadthBar: FC<{ universeId: number; name: string }> = ({ universeId, name }) => {
    // Retries while the backend reports the payload as still building.
    const { data: breadth } = usePulseData((signal) => getPulseBreadth(universeId, signal), [universeId], { refreshMs: 3 * 60_000, pendingRetryMs: 10_000 });

    if (!breadth || (breadth as any).pending || breadth.universeId !== universeId || breadth.advancers === null || breadth.advancers === undefined) {
        return <div className={`${styles.breadth} ${styles.faint}`}>Counting rising and falling members…</div>;
    }
    const up = breadth.advancers || 0;
    const down = breadth.decliners || 0;
    const total = up + down + (breadth.unchanged || 0) || 1;
    return (
        <div className={styles.breadth} title={`${name} members today: ${up} rising, ${down} falling, ${breadth.unchanged || 0} unchanged`}>
            <span className={styles.breadthLabel}>{name} members</span>
            <b className={styles.up}>{up} up</b>
            <span className={styles.breadthBar} aria-hidden="true">
                <span style={{ width: `${(up / total) * 100}%`, background: "var(--mp-up)" }} />
                <span style={{ width: `${(down / total) * 100}%`, background: "var(--mp-down)", marginLeft: "auto" }} />
            </span>
            <b className={styles.down}>{down} down</b>
            {breadth.newHighs !== null && breadth.newLows !== null && (
                <span className={styles.breadthExtra}>
                    52-week highs <b>{breadth.newHighs}</b> · lows <b>{breadth.newLows}</b>
                </span>
            )}
        </div>
    );
};

/** Index list with live day change, the intraday chart for the chosen one, its breadth and the day's movers. */
const IndexPanel: FC<{ mode: SessionMode; universeId: number }> = ({ mode, universeId }) => {
    const [indexes, setIndexes] = useState<IndexRow[]>([]);
    const [activeName, setActiveName] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getIndexes()
            .then((data) => {
                const rows: IndexRow[] = data.rows.map((row) => ({ name: row.name, dailyChange: row.dailyChange, universeId: row.universeId, ISIN: row.webSocketId }));
                setIndexes(rows);
            })
            .catch(() => setIndexes([]))
            .finally(() => setLoading(false));
    }, []);

    // Opens on the region's lead index (Europe while the US is shut); a click is the reader's choice until the region changes.
    useEffect(() => {
        if (!indexes.length) return;
        const preferred = universeId === 10 ? PREFERRED.pre : PREFERRED[mode];
        const wanted = preferred.find((name) => indexes.some((row) => row.name.toUpperCase() === name.toUpperCase()));
        setActiveName(indexes.find((row) => row.name.toUpperCase() === (wanted || "").toUpperCase())?.name || indexes[0]?.name || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indexes, universeId]);

    const active = useMemo(() => indexes.find((row) => row.name === activeName), [indexes, activeName]);

    return (
        <section className={styles.panel} aria-label="Indexes">
            <div className={styles.panelHead}>
                <h2>Indexes</h2>
                <Tooltip title="Live change on the session for each index; pick one to chart it, see how many of its members are rising and falling, and today's biggest movers." arrow placement="top">
                    <span className={styles.info} tabIndex={0} aria-label="About this panel">
                        <InfoOutlinedIcon fontSize="inherit" />
                    </span>
                </Tooltip>
                <span className={styles.spacer} />
                <Link href="/indexes" passHref legacyBehavior>
                    <a className={styles.expand}>All indexes ↗</a>
                </Link>
            </div>
            <div className={styles.indexBody}>
                <div className={styles.indexList} role="group" aria-label="Choose index">
                    {loading ? (
                        <Skeleton variant="rounded" width="100%" height="100%" loading />
                    ) : (
                        indexes.map((index) => <IndexButton key={index.ISIN || index.name} index={index} active={index.name === activeName} onSelect={() => setActiveName(index.name)} />)
                    )}
                </div>
                <div className={styles.indexMain}>
                    <div className={styles.chartBox}>{active ? <IndexesChart activeIndex={active as any} /> : <Skeleton variant="rounded" width="100%" height="100%" loading />}</div>
                    {active && <BreadthBar universeId={active.universeId} name={active.name} />}
                </div>
            </div>
            <MoversStrip universeId={universeId} />
        </section>
    );
};

export default IndexPanel;
